import { word } from "@modules/core/utils/i18n"
import {
  PROGRAM_BUNDLE_DESCRIPTION_MAX_LENGTH,
  PROGRAM_BUNDLE_NAME_MAX_LENGTH,
  ProgramBundleType,
} from "@modules/programBundle/api/programBundleDto"
import { ProgramVisibility } from "@modules/programs/programTypes"
import * as Yup from "yup"

export type ProgramBundleFormInputKey =
  | "name"
  | "description"
  | "imageWeb"
  | "type"
  | "visibility"
  | "programIds"
  | "priceInput"
  | "price"
  | "addFreeTrial"
  | "trialDuration"

export function getValidationProgramBundleSchema(pageNumber: number, maxPrice: number, minPrice: number) {
  switch (pageNumber) {
    case 1:
      return firstPageValidation()
    case 2:
      return secondPageValidation()
    case 3:
      return thirdPageValidation(maxPrice, minPrice)
    default:
      return Yup.object({}) // Need validation for this
  }
}

const firstPageValidation = () => {
  return Yup.object({
    name: Yup.string()
      .max(
        PROGRAM_BUNDLE_NAME_MAX_LENGTH,
        word("global.form.maximumTextValue", { maxTextValue: PROGRAM_BUNDLE_NAME_MAX_LENGTH }),
      )
      .required(word("programBundle.form.name.required")),
    description: Yup.string()
      .max(
        PROGRAM_BUNDLE_DESCRIPTION_MAX_LENGTH,
        word("global.form.maximumTextValue", { maxTextValue: PROGRAM_BUNDLE_DESCRIPTION_MAX_LENGTH }),
      )
      .required(word("programBundle.form.description.required")),
    type: Yup.string()
      .oneOf(Object.values(ProgramBundleType))
      .required(word("programBundle.form.typeOfBundle.required")),
    visibility: Yup.string()
      .oneOf(Object.values(ProgramVisibility))
      .required(word("programBundle.form.visibility.required")),
  })
}

const secondPageValidation = () => {
  return Yup.object({
    programIds: Yup.array()
      .of(Yup.string())
      .min(1, word("programBundle.form.programs.atLeastOneProgramIsRequired"))
      .required(word("programBundle.form.programs.required")),
  })
}

const thirdPageValidation = (maxPrice: number, minPrice: number) => {
  return Yup.object({
    price: Yup.object({
      value: Yup.number()
        .max(maxPrice, word("global.form.maximumValue", { maxValue: maxPrice }))
        .min(minPrice, word("global.form.minimumValue", { minValue: minPrice }))
        .required(word("programBundle.form.price.required")),
      currency: Yup.string().required(),
    }).nullable(),
    priceInput: Yup.number().when("price", {
      is: (val: number | null) => val !== null,
      then: () =>
        Yup.number()
          .max(maxPrice, word("global.form.maximumValue", { maxValue: maxPrice }))
          .min(minPrice, word("global.form.minimumValue", { minValue: minPrice }))
          .required(word("programBundle.form.price.required")),
      otherwise: () => Yup.number().nullable(),
    }),
    trialDuration: Yup.number().when("addFreeTrial", {
      is: (val: boolean) => val,
      then: () =>
        Yup.number()
          .min(1, word("program.duration.minimum.message"))
          .max(365, word("program.duration.maximum.message"))
          .required(word("global.form.required"))
          .integer(),
      otherwise: () => Yup.number().nullable(),
    }),
  })
}
